import z from "zod";
import { PaymentTerm, PaymentTerms } from "../../types";

export const hourOptions = Array.from({ length: 24 }, (_, i) => {
  const displayHour = i % 12 === 0 ? 12 : i % 12;
  const suffix = i < 12 ? "AM" : "PM";
  return {
    label: `${displayHour}:00 ${suffix}`,
    value: String(i),
  };
});

export const getHourOption = (hour: number | null | undefined) =>
  hour != null ? hourOptions.find((o) => o.value === String(hour)) : undefined;

// `yyyy-MM-dd` end to end: what `<input type="date">` reads and writes, and the keys compare
// lexicographically as dates.
export const toDateKey = (date: string | Date) =>
  (date instanceof Date ? date : new Date(date)).toISOString().slice(0, 10);

export const todayKey = () => toDateKey(new Date());

// Hidden, not deleted: the backend's delete is scoped to the future window, so omitting these
// from a save keeps them.
export const isExpiredRange = (range: { toDate: string }) => range.toDate < todayKey();

export const RentalListingSchema = () => {
  return z.object({
    documentUids: z.array(z.string()).optional(),
    propertyRental: propertyRentalSchema,
    isRented: z.boolean().optional(),
  });
};

export const propertyRentalSchema = z
  .object({
    rentalPrice: z.coerce.number().positive().optional(),
    annualIncreasePercent: z.coerce.number().positive().optional(),
    availableFrom: z.coerce.date().optional(),
    showingFrom: z.coerce.date().optional(),
    minimumStayMonths: z.coerce.number().positive().optional(),
    insuranceDepositRequired: z.boolean().optional(),
    noBrokerCommission: z.boolean().optional(),
    furnished: z.boolean().optional(),
    paymentTerms: z
      .object({
        label: z.enum(Object.values(PaymentTerms) as [string, ...string[]]),
        value: z.enum(Object.keys(PaymentTerms) as [PaymentTerm]),
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.availableFrom && data.showingFrom) {
        return data.availableFrom >= data.showingFrom;
      }
      return true;
    },
    {
      message: "Rental start date is before showing start date",
      path: ["availableFrom"],
    }
  );

export const HospitalityListingSchema = () => {
  return z
    .object({
      documentUids: z.array(z.string()).min(1, "At least one photo is required"),
      name: z.string().min(1, "Listing name is required"),
      nameAr: z.string().optional(),
      description: z.string().optional(),
      descriptionAr: z.string().optional(),
      price: z.coerce.number().positive("Price is required"),
      priceForExtraPerson: z.coerce.number().positive().optional(),
      currencyCode: z.object({
        label: z.string(),
        value: z.string(),
      }),
      checkInTimeStart: z
        .object({
          label: z.string(),
          value: z.string(),
        })
        .optional(),
      checkInTimeEnd: z
        .object({
          label: z.string(),
          value: z.string(),
        })
        .optional(),
      checkOutTime: z
        .object({
          label: z.string(),
          value: z.string(),
        })
        .optional(),
      roomType: z
        .object({
          label: z.string(),
          value: z.string(),
        })
        .optional(),
      minNights: z.coerce.number().int().positive().optional(),
      maxNights: z.coerce.number().int().positive().optional(),
      airbnbOfficialListingMarkup: z.coerce.number().optional(),
      bookingEngineMarkup: z.coerce.number().optional(),
      isActive: z.boolean(),
      isFeatured: z.boolean().optional(),
      instantBookable: z.boolean({
        required_error: "Instant bookable is required",
        invalid_type_error: "Instant bookable is required",
      }),
      maxNumberOfGuests: z.coerce.number().int().positive().optional(),
      singleBedCount: z.coerce.number().int().min(0).optional(),
      kingBedCount: z.coerce.number().int().min(0).optional(),
      twinBedCount: z.coerce.number().int().min(0).optional(),
      queenBedCount: z.coerce.number().int().min(0).optional(),
      sofaBedCount: z.coerce.number().int().min(0).optional(),
      viewType: z
        .object({
          label: z.string(),
          value: z.string(),
        })
        .optional(),
      isExtraBedAllowed: z.boolean().optional(),
      isSmokingAllowed: z.boolean().optional(),
      isPetsAllowed: z.boolean().optional(),
      isPartiesAllowed: z.boolean().optional(),
      exceptionalPrices: z
        .array(
          z.object({
            fromDate: z.string().min(1, "From date is required"),
            toDate: z.string().min(1, "To date is required"),
            price: z.coerce.number().positive("Price must be greater than 0"),
          })
        )
        .default([]),
    })
    .superRefine((data, ctx) => {
      const today = todayKey();

      data.exceptionalPrices.forEach((range, index) => {
        if (!range.fromDate || !range.toDate) return;

        if (range.toDate < range.fromDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "To date cannot be earlier than from date",
            path: ["exceptionalPrices", index, "toDate"],
          });
        } else if (range.toDate < today) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Exceptional dates cannot be in the past",
            path: ["exceptionalPrices", index, "toDate"],
          });
        }
      });

      // Ranges are inclusive on both ends, so touching endpoints already overlap.
      const ordered = data.exceptionalPrices
        .map((range, index) => ({ ...range, index }))
        .filter((range) => range.fromDate && range.toDate && range.toDate >= range.fromDate)
        .sort((a, b) => a.fromDate.localeCompare(b.fromDate));

      ordered.forEach((range, position) => {
        const previous = ordered[position - 1];
        if (previous && range.fromDate <= previous.toDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This range overlaps another exceptional date range",
            path: ["exceptionalPrices", range.index, "fromDate"],
          });
        }
      });
    })
    .refine(
      (data) => {
        if (
          data.minNights != null &&
          data.maxNights != null
        ) {
          return data.minNights <= data.maxNights;
        }
        return true;
      },
      {
        message: "Minimum nights must not exceed maximum nights",
        path: ["minNights"],
      }
    )
    .refine(
      (data) => {
        if (
          data.checkInTimeStart?.value != null &&
          data.checkInTimeEnd?.value != null
        ) {
          return Number(data.checkInTimeStart.value) <= Number(data.checkInTimeEnd.value);
        }
        return true;
      },
      {
        message: "Check-in start time must be before check-in end time",
        path: ["checkInTimeStart"],
      }
    );
};

export const resaleDocumentsSchema = z.object({
  floorPlanUids: z.array(z.string()).optional(),
  mediaUids: z.array(z.string()).optional(),
});
export type ResaleDocumentsFormFields = z.infer<typeof resaleDocumentsSchema>;

export const listingDescriptionSchema = z.object({
  descriptionAr: z
    .string()
    .nullish()
    .transform((v) => (v === "null" ? null : v)),
  descriptionEn: z
    .string()
    .nullish()
    .transform((v) => (v === "null" ? null : v)),
});

export type ListingDescriptionFormFields = z.infer<
  typeof listingDescriptionSchema
>;
